import { action, comparer, computed, IReactionDisposer, makeObservable, observable, reaction } from 'mobx';

import { IApiClient } from '../../../app/data/client';
import { debounce } from '../../../app/shared/common';
import Logger from '../../../app/shared/Logger';
import { ICancellableTask, RunLastTasksScheduler } from '../../../app/shared/TasksScheduler';
import { SEARCH_DEBOUNCE_MS, type LoadingState } from '../../constants';
import { PricelistListingTranslation } from '../../localization/ModuleTranslation';
import type { CustomerPricelistData, GetCustomerPricelistsQueryResponse, PricingContext } from '../../model';
import { ModuleRoutes } from '../../ModuleRoutes';
import { DeleteCustomerPricelistsCommand, GetCustomerPricelistsQuery, PRICELISTS_DEFAULT_SORT } from '../../queries';
import ConfirmationModalState from '../../shared/ConfirmationModal/ConfirmationModalState';
import CreatePricelistFormState from '../../shared/PricelistForm/CreatePricelistFormState';
import EditPricelistFormState from '../../shared/PricelistForm/EditPricelistFormState';
import PricelistFormModalState from '../../shared/PricelistFormModal/PricelistFormModalState';
import PricelistListingItemState from './PricelistListingItem/PricelistListingItemState';
import { PricelistListingQueryParser } from './PricelistListingQuery';

/** Collections per request. Sized so one page fills the card grid past the sentinel's prefetch margin. */
const PAGE_SIZE = 24;

/**
 * One request for a page of collections, and whatever it brought back. `RunLastTasksScheduler` flags
 * the task `cancelled` as soon as a newer request starts, so once the run is awaited the caller can
 * tell an outcome that still matters from one a debounced reload has already superseded.
 */
class LoadPricelistsTask implements ICancellableTask {
  public cancelled = false;
  public response: GetCustomerPricelistsQueryResponse;
  /** Whatever the request rejected with — a `throw` is not obliged to carry an `Error`. */
  public error: unknown;

  public constructor(public readonly skip: number) {}
}

export default class PricelistListingState {
  @observable.ref public items: PricelistListingItemState[] = [];
  @observable public loadingState: LoadingState = 'loading';
  @observable public searchTerm = '';
  @observable public total = 0;

  public readonly createPricelistModal: PricelistFormModalState<CreatePricelistFormState>;
  public readonly editPricelistModal: PricelistFormModalState<EditPricelistFormState>;
  public readonly confirmationModal = new ConfirmationModalState();

  private readonly loadPricelistsScheduler: RunLastTasksScheduler<LoadPricelistsTask, void>;
  private readonly disposeSearchReaction: IReactionDisposer;
  private isDisposed = false;

  public constructor(
    private readonly client: IApiClient,
    private readonly pricingContext: PricingContext,
    private readonly routes: ModuleRoutes,
    public readonly translation: PricelistListingTranslation,
    query: Map<string, string> = new Map<string, string>(),
  ) {
    makeObservable(this);

    this.searchTerm = PricelistListingQueryParser.toModel(query).phrase;
    this.createPricelistModal = new PricelistFormModalState(
      this.translation.createPricelistModal,
      new CreatePricelistFormState(this.client, this.translation.createPricelistModal.form),
      this.loadPricelists,
    );
    this.editPricelistModal = new PricelistFormModalState(
      this.translation.pricelistList.itemActions.edit.modal,
      new EditPricelistFormState(this.client, this.translation.pricelistList.itemActions.edit.modal.form),
      this.loadPricelists,
    );

    this.loadPricelistsScheduler = new RunLastTasksScheduler((task) => this.runLoadPricelistsTask(task));

    this.disposeSearchReaction = reaction(
      () => this.searchTerm,
      debounce(() => this.loadPricelists(), SEARCH_DEBOUNCE_MS),
      { equals: comparer.structural },
    );
  }

  @computed
  public get canLoadMorePricelists(): boolean {
    if (this.loadingState === 'loading' || this.loadingState === 'error') {
      return false;
    }

    return this.items.length < this.total;
  }

  @computed
  public get query(): Map<string, string> {
    return PricelistListingQueryParser.toQuery({ phrase: this.searchTerm });
  }

  public get locale(): string {
    return this.pricingContext.languageCode;
  }

  public async onLoad(): Promise<void> {
    this.loadPricelists();
  }

  /** Starts the list over from the first page, for a caller that knows the collections have changed. */
  public reload(): void {
    this.loadPricelists();
  }

  public unload(): void {
    this.isDisposed = true;
    this.disposeSearchReaction();
    this.loadPricelistsScheduler.dispose();
  }

  @action.bound
  public async loadMorePricelists(): Promise<void> {
    if (!this.canLoadMorePricelists || this.loadingState === 'loadingMore') {
      return;
    }

    this.setLoadingState('loadingMore');

    const task = new LoadPricelistsTask(this.items.length);

    await this.loadPricelistsScheduler.run(task);

    if (task.cancelled) {
      return;
    }

    if (task.error) {
      Logger.warn(`Unable to load more price lists. ${task.error}`);
      this.setLoadingState('error');

      return;
    }

    if (task.response.pricelists.empty()) {
      this.setTotal(this.items.length);
      this.setLoadingState('idle');

      return;
    }

    this.appendItems(task.response.pricelists);
    this.setTotal(task.response.total);
    this.setLoadingState('idle');
  }

  @action.bound
  public setSearchTerm(searchTerm: string): void {
    this.searchTerm = searchTerm;
  }

  public deletePricelist(pricelist: CustomerPricelistData): void {
    const translation = this.translation.pricelistList.itemActions.delete;

    this.confirmationModal.ask({
      translation: translation.modal,
      message: translation.confirmationFormat.interpolate([['name', pricelist.name]]),
      successMessage: translation.successFormat.interpolate([['name', pricelist.name]]),
      run: async () => {
        await this.client.send(new DeleteCustomerPricelistsCommand({ pricelistIds: [pricelist.id] }));
        await this.loadPricelists();
      },
    });
  }

  @action.bound
  private async loadPricelists(): Promise<void> {
    // Disposing stops the reaction but cannot clear a debounced reload it has already scheduled,
    // so the last one can still land on a page the user has left.
    if (this.isDisposed) {
      return;
    }

    this.setLoadingState('loading');

    const task = new LoadPricelistsTask(0);

    await this.loadPricelistsScheduler.run(task);

    if (task.cancelled) {
      return;
    }

    if (task.error) {
      Logger.error(`Unable to load the price lists. Error: ${task.error}`);
      this.setItems([]);
      this.setTotal(0);
      this.setLoadingState('error');

      return;
    }

    this.setItems(task.response.pricelists);
    this.setTotal(task.response.total);
    this.setLoadingState('idle');
  }

  private async runLoadPricelistsTask(task: LoadPricelistsTask): Promise<void> {
    try {
      task.response = await this.fetchPricelists(task.skip);
    } catch (error) {
      task.error = error;
    }
  }

  private async fetchPricelists(skip: number): Promise<GetCustomerPricelistsQueryResponse> {
    return this.client.send(
      new GetCustomerPricelistsQuery({
        searchPhrase: this.searchTerm,
        sort: PRICELISTS_DEFAULT_SORT,
        skip,
        take: PAGE_SIZE,
      }),
    );
  }

  private buildItems(pricelists: CustomerPricelistData[]): PricelistListingItemState[] {
    return pricelists.map(
      (pricelist) =>
        new PricelistListingItemState(
          pricelist,
          this.routes,
          this.locale,
          this.translation.pricelistList.pricelistListingItem,
        ),
    );
  }

  @action.bound
  private setItems(pricelists: CustomerPricelistData[]): void {
    this.items = this.buildItems(pricelists);
  }

  @action.bound
  private appendItems(pricelists: CustomerPricelistData[]): void {
    this.items = [...this.items, ...this.buildItems(pricelists)];
  }

  @action.bound
  private setLoadingState(loadingState: LoadingState): void {
    this.loadingState = loadingState;
  }

  @action.bound
  private setTotal(total: number): void {
    this.total = total;
  }
}
